import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Timeout } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { Container } from '../Container';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton';
import { Link } from '../Link';
import { Stack } from '../Stack';
import { Typography } from '../typography';
import { AlertItemContent, AlertItemIcon, AlertItemWrapper } from './style';
import { AlertItemProps, AlertVariant } from './types';

const getIcon = (variant: AlertVariant) => {
    switch (variant) {
        case AlertVariant.Info:
            return IconName.Info;
        case AlertVariant.Success:
            return IconName.CheckCircle;
        case AlertVariant.Warning:
            return IconName.Problematic;
        case AlertVariant.Error:
            return IconName.AlertCircle;
        default:
            return IconName.Info;
    }
};

const useActionComponent = (action: AlertItemProps['action']) => {
    const memoizedLink = useMemo(
        () =>
            isActionObjectWithClick(action) ? (
                <Link
                    as="button"
                    standalone
                    // eslint-disable-next-line react/jsx-handler-names
                    onClick={action.onClick}
                    data-test-alert-action
                    variant="basic"
                    strong
                    size="small"
                >
                    {action.text}
                </Link>
            ) : null,
        [action]
    );

    if (!action) {
        return null;
    }

    if (isActionObjectWithClick(action)) {
        return memoizedLink;
    }

    return action;
};

const isActionObjectWithClick = (action: AlertItemProps['action']): action is { text: string; onClick: () => void } =>
    typeof action === 'object' && action !== null && 'text' in action && 'onClick' in action;

export const AlertItem = ({
    variant,
    title,
    description,
    onClick,
    onClose,
    closeTimeout = 800,
    action,
    closeButtonLabel,
    icon,
    ...other
}: AlertItemProps) => {
    const timeout = useRef<Timeout | null>(null);
    const actionComponent = useActionComponent(action);

    const setCloseTimeout = useCallback(() => {
        timeout.current = new Timeout(closeTimeout, () => {
            onClose();
        });
    }, [closeTimeout, onClose]);

    const handleMouseEnter = () => {
        timeout.current?.pause();
    };

    const handleMouseLeave = () => {
        timeout.current?.resume();
    };

    const clearCloseTimeout = useCallback(() => {
        timeout.current?.remove();
        timeout.current = null;
    }, [timeout]);

    const handleCloseClick = () => {
        clearCloseTimeout();
        onClose();
    };

    useEffect(() => {
        setCloseTimeout();

        return () => {
            clearCloseTimeout();
        };
    }, [clearCloseTimeout, setCloseTimeout]);

    return (
        <AlertItemWrapper
            data-test-alert={variant}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            {...other}
        >
            <AlertItemIcon alignment="center" variant={variant}>
                <Icon value={(variant === 'info' && icon) || getIcon(variant)} />
            </AlertItemIcon>
            <AlertItemContent spacing="50" vertical>
                {title ? (
                    <Typography textStyle="bodyMediumStrong" data-test-alert-title as="div">
                        {title}
                    </Typography>
                ) : null}
                <Stack vertical spacing="100">
                    {description ? (
                        <Typography textStyle="bodyMedium" data-test-alert-description as="div">
                            {description}
                        </Typography>
                    ) : null}
                    {actionComponent}
                </Stack>
            </AlertItemContent>

            <Container padding="150">
                <IconButton
                    variant="ghost"
                    size="small"
                    icon={IconName.CrossClose}
                    onClick={handleCloseClick}
                    data-test-alert-button-close
                    accessibleText={closeButtonLabel}
                    type="submit"
                />
            </Container>
        </AlertItemWrapper>
    );
};
